import { Anchor, Box, Button, Container, Group, Paper, Stack, Text, Title } from '@mantine/core';
import { IconMapPin, IconPhone } from '@tabler/icons-react';
import { useState, type ReactNode } from 'react';
import type { Shema } from '../api/odjemalec';
import { IME_APLIKACIJE } from '../ime';
import { barve } from '../tema';
import { logoUrl, telefonUrl } from './javno';

type Prodajalec = Shema['JavniProdajalec'];

/**
 * Okvir javnih strani ponudbe: glava prodajalca (logotip, naziv, naslov, klic) ali druga {@code glava} (ponudba vseh)
 * in noga. Vgrajena stran (na spletni strani prodajalca) ima samo kompaktno glavo s klicem, brez logotipa.
 */
export function PostavitevPonudbe({
  prodajalec,
  glava,
  vgrajena,
  children,
}: {
  prodajalec?: Prodajalec;
  glava?: ReactNode;
  vgrajena: boolean;
  children: ReactNode;
}) {
  return (
    <Box mih="100dvh" bg={barve.ozadjeStrani} pb={{ base: 96, sm: 'xl' }}>
      {prodajalec ? <GlavaProdajalca prodajalec={prodajalec} vgrajena={vgrajena} /> : glava}
      <Container size="lg" px={{ base: 'sm', sm: 'md' }} pt="md">
        {children}
      </Container>
      {!vgrajena && (
        <Text size="xs" c="dimmed" ta="center" mt="xl">
          Ponudbo omogoča{' '}
          <Anchor href="https://avtopisarna.si" target="_blank" rel="noopener" size="xs">
            {IME_APLIKACIJE}
          </Anchor>
        </Text>
      )}
    </Box>
  );
}

function GlavaProdajalca({ prodajalec, vgrajena }: { prodajalec: Prodajalec; vgrajena: boolean }) {
  const [brezLogotipa, setBrezLogotipa] = useState(!prodajalec.logo);
  return (
    <Paper radius={0} shadow="xs" py="sm">
      <Container size="lg" px={{ base: 'sm', sm: 'md' }}>
        <Group justify="space-between" wrap="nowrap" gap="sm">
          <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
            {!vgrajena && !brezLogotipa && (
              <img
                src={logoUrl(prodajalec.oznaka!)}
                alt=""
                onError={() => setBrezLogotipa(true)}
                style={{ maxHeight: 48, maxWidth: 'min(140px, 24vw)', objectFit: 'contain', flexShrink: 0 }}
              />
            )}
            <Stack gap={0} style={{ minWidth: 0 }}>
              <Title order={2} size="h4" lineClamp={1}>
                {prodajalec.naziv}
              </Title>
              {prodajalec.naslov && (
                <Group gap={4} wrap="nowrap" visibleFrom="xs">
                  <IconMapPin size={14} color={barve.besediloSekundarno} style={{ flexShrink: 0 }} />
                  <Text size="sm" c="dimmed" truncate>
                    {prodajalec.naslov}
                  </Text>
                </Group>
              )}
            </Stack>
          </Group>
          {prodajalec.telefon && (
            <Button
              component="a"
              href={telefonUrl(prodajalec.telefon)}
              leftSection={<IconPhone size={18} />}
              variant="light"
              style={{ flexShrink: 0 }}
            >
              <Box component="span" visibleFrom="sm">
                {prodajalec.telefon}
              </Box>
              <Box component="span" hiddenFrom="sm">
                Pokliči
              </Box>
            </Button>
          )}
        </Group>
      </Container>
    </Paper>
  );
}
